import { AuthError } from "@/AuthError";
import AuthStore from "@/AuthStore";
import AuthApi from "@/api/AuthApi";
import { Logger } from "@iloq/shared";
import { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";

let retrying = false;
let listeners: IRetryCallback[] = [];

export class AuthInterceptor {
  static setAuthInterceptors(axios: AxiosInstance) {
    AuthInterceptor.setRequestAuthInterceptor(axios);
    AuthInterceptor.setResponseAuthInterceptor(axios);
  }

  private static addListener(cb: (authToken: string) => void, failCb: (err: Error) => void): void {
    listeners.push({
      retry: cb,
      fail: failCb,
    });
  }

  private static setRequestAuthInterceptor(axios: AxiosInstance): void {
    axios.interceptors.request.use(
      (config: AxiosRequestConfig) => {
        if (AuthStore.hasRefreshToken()) {
          config.headers.Authorization = `Bearer ${AuthStore.loadAuthToken()}`;
        }
        return config;
      },
      (error: AxiosError) => Promise.reject(error),
    );
  }

  private static refreshAuthToken(): Promise<any> {
    retrying = true;
    AuthStore.removeAuthToken();
    const refreshToken = AuthStore.loadRefreshToken();
    if (refreshToken) {
      Logger.debug("fetching new auth token");
      const authApi = new AuthApi();
      return authApi.fetchAuthToken(refreshToken);
    }
    return Promise.reject(new AuthError(new Error("No refresh token, should login")));
  }

  private static onAuthTokenFetched(authToken: string): void {
    retrying = false;
    Logger.debug("Auth token automatically refreshed:", authToken);
    AuthStore.saveAuthToken(authToken);
    listeners = listeners.filter((listener) => listener.retry(authToken));
  }

  private static onAuthTokenFetchError(error: Error): void {
    retrying = false;
    Logger.error("Automatic auth token refresh failed:", error);
    listeners = listeners.filter((listener) => listener.fail(error));
  }

  private static handleAuthTokenRefresh(axios: AxiosInstance, config: any): Promise<any> {
    const retryOriginalRequest = new Promise((resolve, reject) => {
      AuthInterceptor.addListener(
        (authToken: string) => {
          config.headers.Authorization = `Bearer ${authToken}`;
          resolve(axios(config));
        },
        (err: Error) => {
          reject(err);
        },
      );
    });

    if (!retrying) {
      AuthInterceptor.refreshAuthToken()
        .then(AuthInterceptor.onAuthTokenFetched)
        .catch(AuthInterceptor.onAuthTokenFetchError);
    }
    return retryOriginalRequest;
  }

  private static setResponseAuthInterceptor(axios: AxiosInstance): void {
    axios.interceptors.response.use(
      (response: AxiosResponse) => {
        return Promise.resolve(response);
      },
      (error) => {
        const {
          config,
          response: { status },
        } = error;
        if (status === 401) {
          Logger.debug("got 401, need to refresh auth token");
          return AuthInterceptor.handleAuthTokenRefresh(axios, config);
        }
        return Promise.reject(error);
      },
    );
  }
}

interface IRetryCallback {
  retry(authToken: string): void;
  fail(err: Error): void;
}
